import { config } from '../config';
import { spriteUrl } from '../vue/utils/spriteUrl';

function chevronSvg(className: string): string {
  return `<svg class="${className}" role="presentation" focusable="false"><use href="${spriteUrl('chevron-right-current')}"></use></svg>`;
}

interface NavigationOptions {
  primaryNav: string;
  primaryNavList: string;
  navLinks: string;
  mobileNavIcon: string;
  mobileNavIconClose: string;
  navButtons: string;
  subNavs: string;
  activeSubButton: HTMLElement | null;
}

interface MegaMenuGrandchild {
  name: string;
  url: string;
  target: string;
}

interface MegaMenuCardLink {
  name: string;
  url: string;
  target: string;
}

interface MegaMenuResponsiveImage {
  lg: string;
  md: string;
  sm: string;
  altText: string;
}

interface MegaMenuChild {
  name: string;
  cardTitle: string;
  cardDescription: string;
  cardLink: MegaMenuCardLink | null;
  cardImage: MegaMenuResponsiveImage | null;
  grandchildLinks: MegaMenuGrandchild[];
  shouldShowSidePanel: boolean;
  partnerClass: string;
  partnershipColor: string | null;
}

interface MegaMenuPanel {
  childLinks: MegaMenuChild[];
}

let megaMenuDataPromise: Promise<(MegaMenuPanel | null)[]> | null = null;
const megaMenuLoadPromises = new WeakMap<HTMLElement, Promise<void>>();

function isDesktopNav(): boolean {
  return window.matchMedia(`(min-width: ${config.breakpoint.lgMin}px)`).matches;
}

function fetchMegaMenuData(): Promise<(MegaMenuPanel | null)[]> {
  const nav = document.querySelector<HTMLElement>('#primary-nav');
  const apiUrl = nav?.dataset.megamenuApi?.trim();
  if (!apiUrl) {
    return Promise.resolve([]);
  }
  if (!megaMenuDataPromise) {
    megaMenuDataPromise = fetch(apiUrl).then(r => {
      if (!r.ok) throw new Error(`Mega-menu API error: ${r.status}`);
      return r.json() as Promise<(MegaMenuPanel | null)[]>;
    }).catch(err => {
      megaMenuDataPromise = null;
      throw err;
    });
  }
  return megaMenuDataPromise;
}

function escHtml(str: string | null | undefined): string {
  if (!str) return '';
  return str
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#039;');
}

function renderMegaMenuPanels(childLinks: MegaMenuChild[], isTs: boolean): string {
  return childLinks.map(level2 => renderPanel(level2, isTs)).join('');
}

function renderPanel(level2: MegaMenuChild, isTs: boolean): string {
  const cardContentClass = isTs ? 'card__contentTs' : 'card__content';
  const cardLinkClass = isTs ? 'card__link card__linkTs' : 'card__link';

  const partnershipStyle = level2.partnershipColor ? ` style="--partnership-color:${escHtml(level2.partnershipColor)}"` : '';

  const grandchildLinksHtml = level2.grandchildLinks
    .map(level3 => {
      const itemClass = level2.partnerClass ? ` ${level2.partnerClass}` : '';
      return `<li class="nav-level-3__item${itemClass}"${partnershipStyle}><a href="${escHtml(level3.url)}" target="${escHtml(level3.target)}" class="nav-level-3__link" aria-label="${escHtml(level3.name)}">${escHtml(level3.name)}</a></li>`;
    })
    .join('');

  let cardHtml = '';
  if (level2.shouldShowSidePanel) {
    let imageHtml = '';
    if (level2.cardImage) {
      const img = level2.cardImage;
      imageHtml = `
        <div class="card__img-holder">
          <picture>
            <source srcset="${escHtml(img.lg)}" media="(min-width: 992px)">
            <source srcset="${escHtml(img.md)}" media="(min-width: 768px)">
            <source srcset="${escHtml(img.sm)}" media="(min-width: 0px)">
            <img src="${escHtml(img.sm)}" alt="${escHtml(img.altText)}" class="card__img img-responsive" loading="lazy">
          </picture>
        </div>`;
    }

    let contentHtml = '';
    if (level2.cardTitle) {
      const linkHtml = level2.cardLink
        ? `<div><a href="${escHtml(level2.cardLink.url)}" target="${escHtml(level2.cardLink.target)}" class="${cardLinkClass}" aria-label="${escHtml(level2.cardLink.name)}">${escHtml(level2.cardLink.name)}${chevronSvg('card__link-chevron')}</a></div>`
        : '';
      const partnerPrefix = level2.partnerClass ? `${level2.partnerClass} ` : '';
      contentHtml = `
        <div class="${partnerPrefix}${cardContentClass}"${partnershipStyle}>
          <div class="card__inner">
            <div class="card__header"><h3 class="card__title">${escHtml(level2.cardTitle)}</h3></div>
            <div class="card__description">${level2.cardDescription ?? ''}</div>
          </div>
          ${linkHtml}
        </div>`;
    }

    cardHtml = `<article class="card mega-menu__card">${imageHtml}${contentHtml}</article>`;
  }

  return `
    <div class="width-full mega-menu-content-wrapper">
      <div class="row mega-menu-content">
        <div class="mega-menu-content__center"><ul class="row nav-level-3">${grandchildLinksHtml}</ul></div>
        <div class="mega-menu-content__right">${cardHtml}</div>
      </div>
    </div>`;
}

function grandchildItemsHtml(links: MegaMenuGrandchild[]): string {
  return links
    .map(level3 =>
      `<li class="nav-level-3__item"><a href="${escHtml(level3.url)}" target="${escHtml(level3.target)}" aria-label="${escHtml(level3.name)}" class="nav-level-3__link">${escHtml(level3.name)}</a></li>`
    )
    .join('');
}

function fillMobileGrandchildren(megaMenu: HTMLElement, childLinks: MegaMenuChild[]): void {
  if (!childLinks.length) return;

  const hideLevel2 = childLinks.length === 1;
  if (hideLevel2) {
    const ul = megaMenu.querySelector<HTMLUListElement>(':scope .nav-level-2-wrapper > .nav-level-3');
    if (ul && ul.children.length === 0) {
      ul.innerHTML = grandchildItemsHtml(childLinks[0].grandchildLinks);
    }
    return;
  }

  const items = megaMenu.querySelectorAll<HTMLElement>('.nav-level-2 > li');
  items.forEach((item, i) => {
    const ul = item.querySelector<HTMLUListElement>('.nav-level-3');
    const links = childLinks[i]?.grandchildLinks ?? [];
    if (ul && ul.children.length === 0) {
      ul.innerHTML = grandchildItemsHtml(links);
    }
  });
}

function loadMegaMenuPanel(megaMenu: HTMLElement): Promise<void> {
  if (megaMenu.dataset.loaded === 'true') {
    return Promise.resolve();
  }

  const existing = megaMenuLoadPromises.get(megaMenu);
  if (existing) {
    return existing;
  }

  const promise = (async () => {
    megaMenu.dataset.loading = 'true';
    megaMenu.setAttribute('aria-busy', 'true');

    try {
      const allPanels = await fetchMegaMenuData();
      const menuIndex = parseInt(megaMenu.dataset.menuIndex ?? '0', 10);
      const panelData = allPanels[menuIndex];

      if (panelData) {
        const inner = megaMenu.querySelector<HTMLElement>('.mega-menu__inner');
        if (inner && !inner.querySelector('.mega-menu-content-wrapper')) {
          const nav = document.querySelector<HTMLElement>('#primary-nav');
          const isTs = nav?.dataset.brand === 'travelsphere';
          inner.insertAdjacentHTML('beforeend', renderMegaMenuPanels(panelData.childLinks, isTs));
        }
        fillMobileGrandchildren(megaMenu, panelData.childLinks);
      }

      megaMenu.dataset.loaded = 'true';
      megaMenu.removeAttribute('data-loading');
      megaMenu.removeAttribute('aria-busy');

      // Desktop-only: tab buttons duplicate .mobile-heading in the accordion.
      if (isDesktopNav()) {
        const globalMenuIndex = [...document.querySelectorAll('.mega-menu')].indexOf(megaMenu);
        createDesktopTabsForMenu(megaMenu, globalMenuIndex);
      }
    } catch {
      megaMenu.removeAttribute('data-loading');
      megaMenu.removeAttribute('aria-busy');
    } finally {
      megaMenuLoadPromises.delete(megaMenu);
    }
  })();

  megaMenuLoadPromises.set(megaMenu, promise);
  return promise;
}

interface TabGroup {
  element: HTMLElement;
  buttons: HTMLElement[];
  panels: HTMLElement[];
  firstButton: HTMLElement | null;
  lastButton: HTMLElement | null;
}

const OPTIONS: NavigationOptions = {
  primaryNav: '#primary-nav',
  primaryNavList: '#primary-nav-list',
  navLinks: '#primary-nav-list li',
  mobileNavIcon: '.mobile-nav-icon',
  mobileNavIconClose: '.mobile-nav-icon-close',
  navButtons: '.nav-button',
  subNavs: '.nav-level-2',
  activeSubButton: null
};

const KEY_CODES = {
  ENTER: 13,
  SPACE: 32,
  ESCAPE: 27,
  ARROW_UP: 38,
  ARROW_DOWN: 40,
  TAB: 9
} as const;

const CLASSES = {
  ACTIVE: 'is-active',
  NAV_ACTIVE: 'nav-active',
  MOBILE_NAV_ALT: 'mobile-nav-icon-close',
  MEGA_ACTIVE: 'mega-active',
  HIDDEN: 'hidden',
  JS_LOADED: 'js-loaded',
  TAB_PANEL: 'js-tab-panel',
  TAB_BUTTON: 'js-tab-button'
} as const;

const ATTRIBUTES = {
  ARIA_EXPANDED: 'aria-expanded',
  ARIA_HIDDEN: 'aria-hidden',
  ARIA_CONTROLS: 'aria-controls',
  ARIA_LABELLEDBY: 'aria-labelledby',
  ARIA_SELECTED: 'aria-selected',
  TABINDEX: 'tabindex',
  ROLE: 'role',
  ID: 'ID'
} as const;

let mobileNavListClickHandler: ((e: Event) => void) | null = null;
let mobileIconClickHandler: ((e: Event) => void) | null = null;
let desktopMegaCloseHandler: ((e: Event) => void) | null = null;
let desktopNavMouseDownHandler: ((e: MouseEvent) => void) | null = null;
let desktopNavKeydownHandler: ((e: KeyboardEvent) => void) | null = null;
let desktopOutsideClickHandler: ((e: Event) => void) | null = null;
let desktopFocusInHandler: ((e: FocusEvent) => void) | null = null;

const megaMenuTabDelegates: Array<{
  tablist: HTMLElement;
  click: (e: Event) => void;
  keydown: (e: KeyboardEvent) => void;
}> = [];

function teardownDesktopDelegatedListeners(): void {
  const primaryNav = document.querySelector<HTMLElement>(OPTIONS.primaryNav);

  if (desktopMegaCloseHandler && primaryNav) {
    primaryNav.removeEventListener('click', desktopMegaCloseHandler);
  }
  desktopMegaCloseHandler = null;

  if (desktopNavMouseDownHandler && primaryNav) {
    primaryNav.removeEventListener('mousedown', desktopNavMouseDownHandler);
  }
  desktopNavMouseDownHandler = null;

  if (desktopNavKeydownHandler && primaryNav) {
    primaryNav.removeEventListener('keydown', desktopNavKeydownHandler);
  }
  desktopNavKeydownHandler = null;

  if (desktopOutsideClickHandler) {
    document.removeEventListener('click', desktopOutsideClickHandler);
  }
  desktopOutsideClickHandler = null;

  if (desktopFocusInHandler) {
    document.removeEventListener('focusin', desktopFocusInHandler);
  }
  desktopFocusInHandler = null;
}

function toggleMobileSubmenu(button: HTMLElement, submenu: HTMLElement): void {
  const isExpanded = button.getAttribute(ATTRIBUTES.ARIA_EXPANDED) === 'true';
  const newState = !isExpanded;

  button.setAttribute(ATTRIBUTES.ARIA_EXPANDED, newState.toString());
  button.classList.toggle(CLASSES.ACTIVE);
  submenu.setAttribute(ATTRIBUTES.ARIA_HIDDEN, (!newState).toString());

  if (newState) {
    submenu.style.display = 'block';
    submenu.style.height = '0px';
    submenu.style.overflow = 'hidden';
    submenu.style.transition = 'height 0.3s ease-out';

    const naturalHeight = submenu.scrollHeight;

    requestAnimationFrame(() => {
      submenu.style.height = naturalHeight + 'px';
    });

    setTimeout(() => {
      submenu.style.height = '';
      submenu.style.overflow = '';
      submenu.style.transition = '';
    }, 300);
  } else {
    submenu.style.display = 'block';
    submenu.style.height = 'auto';
    submenu.style.overflow = 'hidden';

    const currentHeight = submenu.offsetHeight;

    submenu.style.height = currentHeight + 'px';
    submenu.style.transition = 'height 0.3s ease-in';

    submenu.offsetHeight;

    requestAnimationFrame(() => {
      submenu.style.height = '0px';
    });

    setTimeout(() => {
      submenu.style.display = 'none';
      submenu.style.height = '';
      submenu.style.overflow = '';
      submenu.style.transition = '';
    }, 300);
  }
}

function setHeaderHeight(measuredHeight?: number): void {
  const header = document.querySelector<HTMLElement>('#header, .header');
  if (header) {
    // offsetHeight forces a synchronous layout; prefer the size ResizeObserver already measured.
    const headerHeight = measuredHeight ?? header.offsetHeight;
    document.documentElement.style.setProperty('--header-height', `${headerHeight}px`);
  }
}

export function init(): void {
  const mqlMobile = window.matchMedia(`(max-width: ${config.breakpoint.mdMax}px)`);
  const mqlDesktop = window.matchMedia(`(min-width: ${config.breakpoint.lgMin}px)`);

  const handleMobileNav = (e: MediaQueryListEvent | MediaQueryList): void => {
    if (e.matches) {
      requestAnimationFrame(() => {
        // The ResizeObserver below reports the height on observe and on every change.
        if (typeof ResizeObserver === 'undefined') {
          setHeaderHeight();
        }
        createMobileMenu();
      });
    } else {
      destroyMobileMenu();
    }
  };

  const handleDesktopNav = (e: MediaQueryListEvent | MediaQueryList): void => {
    if (e.matches) {
      requestAnimationFrame(() => {
        createDesktopMenu();
      });
    } else {
      destroyDesktopMenu();
    }
  };

  mqlMobile.addEventListener('change', handleMobileNav);
  mqlDesktop.addEventListener('change', handleDesktopNav);
  
  handleMobileNav(mqlMobile);
  handleDesktopNav(mqlDesktop);

  createNavigation();

  const header = document.querySelector<HTMLElement>('#header, .header');
  if (header && typeof ResizeObserver !== 'undefined') {
    const resizeObserver = new ResizeObserver((entries) => {
      const blockSize = entries[0]?.borderBoxSize?.[0]?.blockSize;
      const measuredHeight = blockSize === undefined ? undefined : Math.round(blockSize);
      requestAnimationFrame(() => {
        setHeaderHeight(measuredHeight);
      });
    });
    resizeObserver.observe(header);
  }
}

function createMobileMenu(): void {
  const megaMenus = document.querySelectorAll<HTMLElement>('.mega-menu');
  megaMenus.forEach(menu => menu.removeAttribute('style'));

  const navLinks = document.querySelectorAll<HTMLElement>(OPTIONS.navLinks);
  navLinks.forEach((link, index) => {
    const submenu = link.querySelector('ul');
    if (!submenu) return;

    const buttonId = `mobile-nav-heading-${index}`;
    const contentId = `mobile-nav-accordion-${index}`;

    const heading = link.querySelector<HTMLElement>('.mobile-heading:first-child');
    if (!heading) return;

    const button = document.createElement('button');
    button.type = 'button';
    button.id = buttonId;
    button.className = 'nav-button';
    button.setAttribute(ATTRIBUTES.ARIA_EXPANDED, 'false');
    button.setAttribute(ATTRIBUTES.ARIA_CONTROLS, contentId);
    button.innerHTML = `${heading.innerHTML}<span role="presentation" class="nav-button__icon"></span>`;

    heading.innerHTML = '';
    heading.appendChild(button);

    submenu.id = contentId;
    submenu.setAttribute(ATTRIBUTES.ARIA_LABELLEDBY, buttonId);
    submenu.setAttribute(ATTRIBUTES.ARIA_HIDDEN, 'true');
  });

  const navList = document.querySelector<HTMLElement>(OPTIONS.primaryNavList);
  if (mobileNavListClickHandler && navList) {
    navList.removeEventListener('click', mobileNavListClickHandler);
  }
  mobileNavListClickHandler = (e: Event) => {
    const btn = (e.target as HTMLElement).closest<HTMLElement>('.nav-button');
    if (!btn || !navList?.contains(btn)) return;
    const contentId = btn.getAttribute(ATTRIBUTES.ARIA_CONTROLS);
    const submenu = contentId ? document.getElementById(contentId) : null;
    if (!(submenu instanceof HTMLElement)) return;

    const megaMenu = btn.closest<HTMLElement>('.mega-menu')
      ?? btn.closest('.nav-level-1__item')?.querySelector<HTMLElement>('.mega-menu');

    if (megaMenu && megaMenu.dataset.loaded !== 'true') {
      e.preventDefault();
      if (megaMenu.dataset.loading === 'true') return;
      loadMegaMenuPanel(megaMenu).then(() => {
        toggleMobileSubmenu(btn, submenu);
      });
      return;
    }

    toggleMobileSubmenu(btn, submenu);
  };
  navList?.addEventListener('click', mobileNavListClickHandler);

  const primaryNav = document.querySelector<HTMLElement>(OPTIONS.primaryNav);
  if (primaryNav) {
    primaryNav.setAttribute(ATTRIBUTES.ARIA_HIDDEN, 'true');

    // Setup open/close icons
    const openIcon = document.querySelector<HTMLElement>(OPTIONS.mobileNavIcon);
    const closeIcon = document.querySelector<HTMLElement>(OPTIONS.mobileNavIconClose);
   
    if (openIcon) {
      openIcon.setAttribute(ATTRIBUTES.ARIA_CONTROLS, primaryNav.id || 'primary-nav');
      openIcon.setAttribute(ATTRIBUTES.ARIA_EXPANDED, 'false');
      openIcon.classList.remove('d-none');
    }
    if (closeIcon) {
      closeIcon.setAttribute(ATTRIBUTES.ARIA_CONTROLS, primaryNav.id || 'primary-nav');
      closeIcon.setAttribute(ATTRIBUTES.ARIA_EXPANDED, 'false');
      closeIcon.classList.add('d-none');
    }

    // Remove previous listeners
    if (mobileIconClickHandler) {
      document.removeEventListener('click', mobileIconClickHandler);
    }

    // Handler for open icon
    const handleOpen = (e: Event) => {
      const icon = (e.target as HTMLElement).closest<HTMLElement>(OPTIONS.mobileNavIcon);
      if (!icon) return;
      e.preventDefault();
     
      primaryNav.setAttribute(ATTRIBUTES.ARIA_HIDDEN, 'false');
      document.documentElement.classList.add(CLASSES.NAV_ACTIVE);
      icon.setAttribute(ATTRIBUTES.ARIA_EXPANDED, 'true');
      // Toggle visibility
      if (openIcon) {
        openIcon.classList.add('d-none');
      }

      if (closeIcon) {
        closeIcon.classList.remove('d-none');
      } 
     
      // Hide usp bars
      const uspBars = document.querySelectorAll<HTMLElement>('.usp-bar, .usp-barTs');
      uspBars.forEach(uspBar => uspBar.classList.add('d-none'));

      fetchMegaMenuData().catch(() => { /* warmup; first accordion retries on failure */ });
    };

    // Handler for close icon
    const handleClose = (e: Event) => {
      const icon = (e.target as HTMLElement).closest<HTMLElement>(OPTIONS.mobileNavIconClose);
      if (!icon) return;
      e.preventDefault();
      
      primaryNav.setAttribute(ATTRIBUTES.ARIA_HIDDEN, 'true');
      document.documentElement.classList.remove(CLASSES.NAV_ACTIVE);
      // Toggle visibility
      if (openIcon) {
        openIcon.classList.remove('d-none');
      }

      if (closeIcon) {
        closeIcon.classList.add('d-none');
      }

      if (openIcon) {
        openIcon.setAttribute(ATTRIBUTES.ARIA_EXPANDED, 'false');
      }
      
      if (closeIcon) {
        closeIcon.setAttribute(ATTRIBUTES.ARIA_EXPANDED, 'false');
      }
     
      // Show usp bars
      const uspBars = document.querySelectorAll<HTMLElement>('.usp-bar, .usp-barTs');
      uspBars.forEach(uspBar => uspBar.classList.remove('d-none'));
    };

    // Attach listeners
    if (openIcon) {
      openIcon.addEventListener('click', handleOpen);
    }
    
    if (closeIcon) {
      closeIcon.addEventListener('click', handleClose);
    }
  }
}

function destroyMobileMenu(): void {
  const navList = document.querySelector<HTMLElement>(OPTIONS.primaryNavList);
  if (mobileNavListClickHandler && navList) {
    navList.removeEventListener('click', mobileNavListClickHandler);
  }
  mobileNavListClickHandler = null;

  if (mobileIconClickHandler) {
    document.removeEventListener('click', mobileIconClickHandler);
  }
  mobileIconClickHandler = null;

  const primaryNav = document.querySelector<HTMLElement>(OPTIONS.primaryNav);
  if (primaryNav) {
    primaryNav.removeAttribute(ATTRIBUTES.ARIA_HIDDEN);
  }

  const navButtons = document.querySelectorAll<HTMLElement>(OPTIONS.navButtons);
  navButtons.forEach(button => {
    const handler = (button as any).__clickHandler;
    if (handler) {
      button.removeEventListener('click', handler);
    }
  });

  const focusElements = document.querySelectorAll<HTMLElement>('.nav-button, .nav-level-1__link, .secondary-nav__link');
  focusElements.forEach(element => {
    const handler = (element as any).__focusHandler;
    if (handler) {
      element.removeEventListener('focus', handler);
    }
  });

  document.documentElement.classList.remove(CLASSES.NAV_ACTIVE);
  
  const uspBars = document.querySelectorAll<HTMLElement>('.usp-bar, .usp-barTs');
  uspBars.forEach(uspBar => {
    uspBar.style.visibility = 'visible';
  });

  const activeButtons = document.querySelectorAll<HTMLElement>('.nav-button.is-active');
  activeButtons.forEach(button => {
    button.click();
  });

  const links = document.querySelectorAll<HTMLElement>('a:not(#primary-nav a)');
  links.forEach(link => {
    link.setAttribute(ATTRIBUTES.TABINDEX, '0');
    link.style.cursor = 'pointer';
  });

  OPTIONS.activeSubButton = null;

  const navItems = document.querySelectorAll<HTMLElement>('.nav-level-1__item');
  navItems.forEach(item => {
    const button = item.querySelector('button');
    if (button) {
      const parent = button.parentElement;
      if (parent) {
        while (button.firstChild) {
          parent.insertBefore(button.firstChild, button);
        }
        parent.removeChild(button);
      }
    }
    
    const icons = item.querySelectorAll<HTMLElement>('.nav-button__icon');
    icons.forEach(icon => icon.remove());
  });

  const navLevels = document.querySelectorAll<HTMLElement>('.nav-level-2, .nav-level-3');
  navLevels.forEach(level => {
    level.removeAttribute(ATTRIBUTES.ARIA_LABELLEDBY);
    level.removeAttribute(ATTRIBUTES.ARIA_HIDDEN);
    level.removeAttribute(ATTRIBUTES.ID);
    level.removeAttribute('style');
  });
}

function createDesktopMenu(): void {
  teardownDesktopDelegatedListeners();

  const primaryNav = document.querySelector<HTMLElement>(OPTIONS.primaryNav);

  const navLinks = document.querySelectorAll<HTMLElement>(OPTIONS.navLinks);
  navLinks.forEach((link, index) => {
    const megaMenu = link.querySelector<HTMLElement>('.mega-menu');
    if (!megaMenu) return;

    const buttonId = `desktop-nav-heading-${index}`;
    const contentId = `desktop-nav-accordion-${index}`;

    const heading = link.querySelector<HTMLElement>('.nav-level-1__heading');
    if (!heading) return;

    const buttonText = heading.innerHTML;
    
    const button = document.createElement('button');
    button.type = 'button';
    button.id = buttonId;
    button.className = 'nav-button';
    button.setAttribute(ATTRIBUTES.ARIA_EXPANDED, 'false');
    button.setAttribute(ATTRIBUTES.ARIA_CONTROLS, contentId);
    button.innerHTML = `${buttonText}<span role="presentation" class="nav-button__icon"></span>`;

    heading.classList.add(CLASSES.JS_LOADED);
    heading.innerHTML = '';
    heading.appendChild(button);

    button.addEventListener('mouseenter', () => {
      loadMegaMenuPanel(megaMenu).catch(() => { /* silently ignore prefetch errors */ });
    }, { passive: true });

    megaMenu.id = contentId;
    megaMenu.setAttribute(ATTRIBUTES.ARIA_LABELLEDBY, buttonId);
    megaMenu.setAttribute(ATTRIBUTES.ARIA_HIDDEN, 'true');
  });

  if (primaryNav) {
    desktopMegaCloseHandler = (e: Event) => {
      if (!(e.target as HTMLElement).closest?.('.js-mega-close')) return;
      closeDesktopNavItem();
    };
    primaryNav.addEventListener('click', desktopMegaCloseHandler);

    desktopNavMouseDownHandler = (event: MouseEvent) => {
      const target = (event.target as HTMLElement).closest<HTMLElement>('.nav-button');
      if (!target || !primaryNav.contains(target)) return;
      if (target.classList.contains(CLASSES.ACTIVE)) {
        closeDesktopNavItem();
      } else {
        openDesktopNavItem(target);
      }
    };
    primaryNav.addEventListener('mousedown', desktopNavMouseDownHandler);

    desktopNavKeydownHandler = (event: KeyboardEvent) => {
      const target = (event.target as HTMLElement).closest<HTMLElement>('.nav-button');
      if (!target || !primaryNav.contains(target)) return;
      if (event.which === KEY_CODES.ENTER || event.which === KEY_CODES.SPACE) {
        event.preventDefault();
        if (target.classList.contains(CLASSES.ACTIVE)) {
          closeDesktopNavItem();
        } else {
          openDesktopNavItem(target);
        }
      }
    };
    primaryNav.addEventListener('keydown', desktopNavKeydownHandler);
  }

  desktopOutsideClickHandler = (event: Event) => {
    const primaryNavEl = document.querySelector<HTMLElement>(OPTIONS.primaryNav);
    if (primaryNavEl && !primaryNavEl.contains(event.target as Node)) {
      closeDesktopNavItem();
    }
  };
  document.addEventListener('click', desktopOutsideClickHandler);

  desktopFocusInHandler = (event: FocusEvent) => {
    if (!OPTIONS.activeSubButton) return;
    const target = event.target;
    if (!(target instanceof HTMLElement)) return;
    const navRoot = document.querySelector<HTMLElement>(OPTIONS.primaryNav);

    if (target.matches('.nav-button') || target.closest('.nav-button')) {
      closeDesktopNavItem();
      return;
    }
    if (target.matches('.nav-level-1__link') || target.closest('.nav-level-1__link')) {
      closeDesktopNavItem();
      return;
    }
    if (target.matches('.secondary-nav__link') || target.closest('.secondary-nav__link')) {
      closeDesktopNavItem();
      return;
    }
    const interactive = target.closest('a, button, input');
    if (interactive && navRoot && !navRoot.contains(interactive)) {
      closeDesktopNavItem();
    }
  };
  document.addEventListener('focusin', desktopFocusInHandler);

  createDesktopTabs();
  createStickyNav();

  // Warm up: prefetch all mega-menu data after the page is idle so the
  // first hover is instant without competing with LCP/FID resources.
  const scheduleWarmUp = (): void => {
    const nav = document.querySelector<HTMLElement>('#primary-nav');
    if (!nav?.dataset.megamenuApi?.trim()) return;
    fetchMegaMenuData().catch(() => { /* silently ignore prefetch errors */ });
  };

  if ('requestIdleCallback' in window) {
    (window as any).requestIdleCallback(scheduleWarmUp, { timeout: 3000 });
  } else {
    setTimeout(scheduleWarmUp, 2000);
  }
}

function openDesktopNavItem(element: HTMLElement): void {
  if (OPTIONS.activeSubButton) {
    closeDesktopNavItem();
  }

  const megaMenu = element.closest('li')?.querySelector<HTMLElement>('.mega-menu');
  if (!megaMenu) return;

  requestAnimationFrame(() => {
    OPTIONS.activeSubButton = element;

    element.classList.add(CLASSES.ACTIVE);
    element.setAttribute(ATTRIBUTES.ARIA_EXPANDED, 'true');

    megaMenu.style.height = megaMenu.offsetHeight + 'px';
    megaMenu.setAttribute(ATTRIBUTES.ARIA_HIDDEN, 'false');
    megaMenu.classList.add(CLASSES.MEGA_ACTIVE);
    megaMenu.style.display = 'block';
    megaMenu.style.opacity = '0';

    requestAnimationFrame(() => {
      megaMenu.style.transition = 'opacity 0.2s ease-in';
      megaMenu.style.opacity = '1';
    });

    // Load panel content on first open, then focus the first tab when ready
    if (megaMenu.dataset.loaded !== 'true') {
      loadMegaMenuPanel(megaMenu).then(() => {
        const firstTab = megaMenu.querySelector<HTMLElement>('.js-tab-button:first-child');
        if (firstTab && OPTIONS.activeSubButton === element) {
          firstTab.focus();
        }
      });
    } else {
      const firstTab = megaMenu.querySelector<HTMLElement>('.js-tab-button:first-child');
      if (firstTab) firstTab.focus();
    }
  });
}

function closeDesktopNavItem(): void {
  if (!OPTIONS.activeSubButton) return;

  const element = OPTIONS.activeSubButton;
  OPTIONS.activeSubButton = null;
  
  element.classList.remove(CLASSES.ACTIVE);
  element.setAttribute(ATTRIBUTES.ARIA_EXPANDED, 'false');

  const megaMenu = element.closest('li')?.querySelector<HTMLElement>('.mega-menu');
  if (megaMenu) {
    megaMenu.setAttribute(ATTRIBUTES.ARIA_HIDDEN, 'true');
    megaMenu.classList.remove(CLASSES.MEGA_ACTIVE);
    
    megaMenu.style.transition = 'opacity 0.2s ease-out';
    megaMenu.style.opacity = '0';
    
    setTimeout(() => {
      megaMenu.style.display = 'none';
      megaMenu.style.opacity = '';
      megaMenu.style.transition = '';
      megaMenu.style.height = '';
      
      // Reset all tabs to inactive
      const tabButtons = megaMenu.querySelectorAll<HTMLElement>('.js-tab-button');
      tabButtons.forEach(button => {
        button.setAttribute(ATTRIBUTES.ARIA_SELECTED, 'false');
        button.setAttribute(ATTRIBUTES.TABINDEX, '-1');
        button.classList.remove(CLASSES.ACTIVE);
      });

      // Reset all panels to hidden
      const tabPanels = megaMenu.querySelectorAll<HTMLElement>('.mega-menu-content-wrapper');
      tabPanels.forEach(panel => {
        panel.setAttribute(ATTRIBUTES.ARIA_HIDDEN, 'true');
        panel.setAttribute(ATTRIBUTES.TABINDEX, '-1');
      });

      // Set first tab and panel as active
      const firstButton = megaMenu.querySelector<HTMLElement>('.js-tab-button:first-child');
      const firstPanel = tabPanels[0];
      
      if (firstButton) {
        firstButton.setAttribute(ATTRIBUTES.ARIA_SELECTED, 'true');
        firstButton.setAttribute(ATTRIBUTES.TABINDEX, '0');
        firstButton.classList.add(CLASSES.ACTIVE);
      }
      
      if (firstPanel) {
        firstPanel.setAttribute(ATTRIBUTES.ARIA_HIDDEN, 'false');
        firstPanel.setAttribute(ATTRIBUTES.TABINDEX, '0');
      }
    }, 200);
  }

  element.focus();
}

function createDesktopTabs(): void {
  megaMenuTabDelegates.forEach(({ tablist, click, keydown }) => {
    tablist.removeEventListener('click', click);
    tablist.removeEventListener('keydown', keydown);
  });
  megaMenuTabDelegates.length = 0;

  const megaMenus = document.querySelectorAll<HTMLElement>('.mega-menu');
  megaMenus.forEach((menu, menuIndex) => {
    const navLevel2 = menu.querySelector<HTMLElement>('.nav-level-2');
    if (!navLevel2) return;

    menu.classList.add('has-tabs');
    navLevel2.setAttribute(ATTRIBUTES.ROLE, 'tablist');
    navLevel2.setAttribute('aria-orientation', 'vertical');

    const tabListItems = navLevel2.querySelectorAll<HTMLElement>(':scope > li');
    const tabGroup: TabGroup = {
      element: menu,
      buttons: [],
      panels: [],
      firstButton: null,
      lastButton: null
    };

    const allPanels = menu.querySelectorAll<HTMLElement>('.mega-menu-content-wrapper');
    
    tabListItems.forEach((item, itemIndex) => {
      const tabButtonId = `tab-heading-${menuIndex}-${itemIndex}`;
      const tabPanelId = `tab-content-${menuIndex}-${itemIndex}`;

      const tabPanel = allPanels[itemIndex];
      if (!tabPanel) return;

      tabPanel.setAttribute(ATTRIBUTES.ROLE, 'tabpanel');
      tabPanel.id = tabPanelId;
      tabPanel.setAttribute(ATTRIBUTES.ARIA_LABELLEDBY, tabButtonId);
      tabPanel.setAttribute(ATTRIBUTES.ARIA_HIDDEN, 'true');
      tabPanel.setAttribute(ATTRIBUTES.TABINDEX, '-1');
      tabPanel.classList.add(CLASSES.TAB_PANEL);

      const tabButtonText = item.querySelector<HTMLElement>('span')?.textContent || '';
      const tabButton = document.createElement('button');
      tabButton.type = 'button';
      tabButton.id = tabButtonId;
      tabButton.className = 'tab-button js-tab-button';
      tabButton.setAttribute(ATTRIBUTES.ROLE, 'tab');
      tabButton.setAttribute(ATTRIBUTES.ARIA_SELECTED, 'false');
      tabButton.setAttribute(ATTRIBUTES.ARIA_CONTROLS, tabPanelId);
      tabButton.setAttribute(ATTRIBUTES.TABINDEX, '-1');
      tabButton.textContent = tabButtonText;

      tabButton.insertAdjacentHTML('beforeend', chevronSvg('tab-button__chevron'));

      item.setAttribute(ATTRIBUTES.ROLE, 'presentation');
      item.insertBefore(tabButton, item.firstChild);

      const span = item.querySelector<HTMLElement>('span');
      if (span) {
        span.classList.add(CLASSES.HIDDEN);
      }

      tabGroup.buttons.push(tabButton);
      tabGroup.panels.push(tabPanel);
    });

    if (tabGroup.buttons.length > 0) {
      tabGroup.firstButton = tabGroup.buttons[0];
      tabGroup.lastButton = tabGroup.buttons[tabGroup.buttons.length - 1];
      
      tabGroup.firstButton.setAttribute(ATTRIBUTES.ARIA_SELECTED, 'true');
      tabGroup.firstButton.setAttribute(ATTRIBUTES.TABINDEX, '0');
      tabGroup.firstButton.classList.add(CLASSES.ACTIVE);
      
      if (tabGroup.panels[0]) {
        tabGroup.panels[0].setAttribute(ATTRIBUTES.ARIA_HIDDEN, 'false');
        tabGroup.panels[0].setAttribute(ATTRIBUTES.TABINDEX, '0');
      }
    }

    const onMegaTabClick = (e: Event): void => {
      const tabButton = (e.target as HTMLElement).closest<HTMLElement>('.js-tab-button');
      if (!tabButton || !navLevel2.contains(tabButton)) return;
      e.preventDefault();

      const tabPanelId = tabButton.getAttribute(ATTRIBUTES.ARIA_CONTROLS);
      const tabPanel = tabPanelId ? document.getElementById(tabPanelId) : null;
      if (!(tabPanel instanceof HTMLElement)) return;

      const activeButton = menu.querySelector<HTMLElement>('.js-tab-button.is-active');
      if (activeButton) {
        activeButton.setAttribute(ATTRIBUTES.ARIA_SELECTED, 'false');
        activeButton.setAttribute(ATTRIBUTES.TABINDEX, '-1');
        activeButton.classList.remove(CLASSES.ACTIVE);
      }

      tabButton.setAttribute(ATTRIBUTES.ARIA_SELECTED, 'true');
      tabButton.setAttribute(ATTRIBUTES.TABINDEX, '0');
      tabButton.classList.add(CLASSES.ACTIVE);
      tabButton.focus();

      const visiblePanel = menu.querySelector<HTMLElement>('.js-tab-panel:not([aria-hidden="true"])');
      if (visiblePanel) {
        visiblePanel.setAttribute(ATTRIBUTES.ARIA_HIDDEN, 'true');
        visiblePanel.setAttribute(ATTRIBUTES.TABINDEX, '-1');
      }

      tabPanel.setAttribute(ATTRIBUTES.ARIA_HIDDEN, 'false');
      tabPanel.setAttribute(ATTRIBUTES.TABINDEX, '0');
    };

    const onMegaTabKeydown = (e: KeyboardEvent): void => {
      const button = (e.target as HTMLElement).closest<HTMLElement>('.js-tab-button');
      if (!button || !navLevel2.contains(button)) return;

      switch (e.which) {
        case KEY_CODES.ARROW_UP: {
          e.preventDefault();
          const prevButton = button.parentElement?.previousElementSibling?.querySelector<HTMLElement>('.js-tab-button');
          if (prevButton) {
            prevButton.click();
          } else if (tabGroup.lastButton) {
            tabGroup.lastButton.click();
          }
          break;
        }
        case KEY_CODES.ARROW_DOWN: {
          e.preventDefault();
          const nextButton = button.parentElement?.nextElementSibling?.querySelector<HTMLElement>('.js-tab-button');
          if (nextButton) {
            nextButton.click();
          } else if (tabGroup.firstButton) {
            tabGroup.firstButton.click();
          }
          break;
        }
        default:
          break;
      }
    };

    navLevel2.addEventListener('click', onMegaTabClick);
    navLevel2.addEventListener('keydown', onMegaTabKeydown);
    megaMenuTabDelegates.push({
      tablist: navLevel2,
      click: onMegaTabClick,
      keydown: onMegaTabKeydown
    });
  });
}

function createDesktopTabsForMenu(menu: HTMLElement, menuIndex: number): void {
  const navLevel2 = menu.querySelector<HTMLElement>('.nav-level-2');
  if (!navLevel2) return;

  // Remove the stale delegate that createDesktopTabs added when panels were absent
  const staleIdx = megaMenuTabDelegates.findIndex(d => d.tablist === navLevel2);
  if (staleIdx !== -1) {
    const { tablist, click, keydown } = megaMenuTabDelegates[staleIdx];
    tablist.removeEventListener('click', click);
    tablist.removeEventListener('keydown', keydown);
    megaMenuTabDelegates.splice(staleIdx, 1);
  }

  // Remove any buttons left from a previous init (e.g. mobile→desktop resize after load)
  menu.querySelectorAll<HTMLElement>('.js-tab-button').forEach(b => b.remove());

  const allPanels = menu.querySelectorAll<HTMLElement>('.mega-menu-content-wrapper');
  if (!allPanels.length) return;

  const tabListItems = navLevel2.querySelectorAll<HTMLElement>(':scope > li');
  const tabGroup: TabGroup = {
    element: menu,
    buttons: [],
    panels: [],
    firstButton: null,
    lastButton: null
  };

  tabListItems.forEach((item, itemIndex) => {
    const tabButtonId = `tab-heading-${menuIndex}-${itemIndex}`;
    const tabPanelId = `tab-content-${menuIndex}-${itemIndex}`;

    const tabPanel = allPanels[itemIndex];
    if (!tabPanel) return;

    tabPanel.setAttribute(ATTRIBUTES.ROLE, 'tabpanel');
    tabPanel.id = tabPanelId;
    tabPanel.setAttribute(ATTRIBUTES.ARIA_LABELLEDBY, tabButtonId);
    tabPanel.setAttribute(ATTRIBUTES.ARIA_HIDDEN, 'true');
    tabPanel.setAttribute(ATTRIBUTES.TABINDEX, '-1');
    tabPanel.classList.add(CLASSES.TAB_PANEL);

    const tabButtonText = item.querySelector<HTMLElement>('span')?.textContent || '';
    const tabButton = document.createElement('button');
    tabButton.type = 'button';
    tabButton.id = tabButtonId;
    tabButton.className = 'tab-button js-tab-button';
    tabButton.setAttribute(ATTRIBUTES.ROLE, 'tab');
    tabButton.setAttribute(ATTRIBUTES.ARIA_SELECTED, 'false');
    tabButton.setAttribute(ATTRIBUTES.ARIA_CONTROLS, tabPanelId);
    tabButton.setAttribute(ATTRIBUTES.TABINDEX, '-1');
    tabButton.textContent = tabButtonText;

    tabButton.insertAdjacentHTML('beforeend', chevronSvg('tab-button__chevron'));

    item.setAttribute(ATTRIBUTES.ROLE, 'presentation');
    item.insertBefore(tabButton, item.firstChild);

    const span = item.querySelector<HTMLElement>('span');
    if (span) span.classList.add(CLASSES.HIDDEN);

    tabGroup.buttons.push(tabButton);
    tabGroup.panels.push(tabPanel);
  });

  if (tabGroup.buttons.length > 0) {
    tabGroup.firstButton = tabGroup.buttons[0];
    tabGroup.lastButton = tabGroup.buttons[tabGroup.buttons.length - 1];

    tabGroup.firstButton.setAttribute(ATTRIBUTES.ARIA_SELECTED, 'true');
    tabGroup.firstButton.setAttribute(ATTRIBUTES.TABINDEX, '0');
    tabGroup.firstButton.classList.add(CLASSES.ACTIVE);

    if (tabGroup.panels[0]) {
      tabGroup.panels[0].setAttribute(ATTRIBUTES.ARIA_HIDDEN, 'false');
      tabGroup.panels[0].setAttribute(ATTRIBUTES.TABINDEX, '0');
    }
  }

  // Re-register delegated handlers with correct tabGroup so arrow-key wrapping works
  const onMegaTabClick = (e: Event): void => {
    const tabButton = (e.target as HTMLElement).closest<HTMLElement>('.js-tab-button');
    if (!tabButton || !navLevel2.contains(tabButton)) return;
    e.preventDefault();

    const tabPanelId = tabButton.getAttribute(ATTRIBUTES.ARIA_CONTROLS);
    const tabPanel = tabPanelId ? document.getElementById(tabPanelId) : null;
    if (!(tabPanel instanceof HTMLElement)) return;

    const activeButton = menu.querySelector<HTMLElement>('.js-tab-button.is-active');
    if (activeButton) {
      activeButton.setAttribute(ATTRIBUTES.ARIA_SELECTED, 'false');
      activeButton.setAttribute(ATTRIBUTES.TABINDEX, '-1');
      activeButton.classList.remove(CLASSES.ACTIVE);
    }

    tabButton.setAttribute(ATTRIBUTES.ARIA_SELECTED, 'true');
    tabButton.setAttribute(ATTRIBUTES.TABINDEX, '0');
    tabButton.classList.add(CLASSES.ACTIVE);
    tabButton.focus();

    const visiblePanel = menu.querySelector<HTMLElement>('.js-tab-panel:not([aria-hidden="true"])');
    if (visiblePanel) {
      visiblePanel.setAttribute(ATTRIBUTES.ARIA_HIDDEN, 'true');
      visiblePanel.setAttribute(ATTRIBUTES.TABINDEX, '-1');
    }

    tabPanel.setAttribute(ATTRIBUTES.ARIA_HIDDEN, 'false');
    tabPanel.setAttribute(ATTRIBUTES.TABINDEX, '0');
  };

  const onMegaTabKeydown = (e: KeyboardEvent): void => {
    const button = (e.target as HTMLElement).closest<HTMLElement>('.js-tab-button');
    if (!button || !navLevel2.contains(button)) return;

    switch (e.which) {
      case KEY_CODES.ARROW_UP: {
        e.preventDefault();
        const prevButton = button.parentElement?.previousElementSibling?.querySelector<HTMLElement>('.js-tab-button');
        if (prevButton) {
          prevButton.click();
        } else if (tabGroup.lastButton) {
          tabGroup.lastButton.click();
        }
        break;
      }
      case KEY_CODES.ARROW_DOWN: {
        e.preventDefault();
        const nextButton = button.parentElement?.nextElementSibling?.querySelector<HTMLElement>('.js-tab-button');
        if (nextButton) {
          nextButton.click();
        } else if (tabGroup.firstButton) {
          tabGroup.firstButton.click();
        }
        break;
      }
      default:
        break;
    }
  };

  navLevel2.addEventListener('click', onMegaTabClick);
  navLevel2.addEventListener('keydown', onMegaTabKeydown);
  megaMenuTabDelegates.push({ tablist: navLevel2, click: onMegaTabClick, keydown: onMegaTabKeydown });
}

function destroyDesktopMenu(): void {
  teardownDesktopDelegatedListeners();

  const activeButtons = document.querySelectorAll<HTMLElement>('.nav-button.is-active');
  activeButtons.forEach(button => button.click());

  const navItems = document.querySelectorAll<HTMLElement>('.nav-level-1__item');
  navItems.forEach(item => {
    const button = item.querySelector('button');
    if (button) {
      const parent = button.parentElement;
      if (parent) {
        while (button.firstChild) {
          parent.insertBefore(button.firstChild, button);
        }
        parent.removeChild(button);
      }
    }
    
    const icons = item.querySelectorAll<HTMLElement>('.nav-button__icon');
    icons.forEach(icon => icon.remove());
  });

  const megaMenus = document.querySelectorAll<HTMLElement>('.mega-menu');
  megaMenus.forEach(menu => {
    menu.removeAttribute(ATTRIBUTES.ARIA_LABELLEDBY);
    menu.removeAttribute(ATTRIBUTES.ARIA_HIDDEN);
    menu.removeAttribute(ATTRIBUTES.ID);
    menu.removeAttribute('style');
    menu.style.zIndex = '-1';
    menu.classList.remove('has-tabs');
  });

  const navLevel2Items = document.querySelectorAll<HTMLElement>('.nav-level-2__item a');
  navLevel2Items.forEach(item => item.classList.remove(CLASSES.HIDDEN));

  const megaCloseButtons = document.querySelectorAll<HTMLElement>('.js-mega-close');
  megaCloseButtons.forEach(button => button.remove());

  destroyDesktopTabs();
}

function destroyDesktopTabs(): void {
  megaMenuTabDelegates.forEach(({ tablist, click, keydown }) => {
    tablist.removeEventListener('click', click);
    tablist.removeEventListener('keydown', keydown);
  });
  megaMenuTabDelegates.length = 0;

  const navLevel2s = document.querySelectorAll<HTMLElement>('.nav-level-2');
  navLevel2s.forEach(level => {
    level.removeAttribute(ATTRIBUTES.ROLE);
    level.removeAttribute('aria-orientation');
  });

  const navLevel2Items = document.querySelectorAll<HTMLElement>('.nav-level-2__item');
  navLevel2Items.forEach(item => {
    item.removeAttribute(ATTRIBUTES.ROLE);
    const span = item.querySelector<HTMLElement>('span');
    if (span) {
      span.classList.remove(CLASSES.HIDDEN);
    }
  });

  const tabButtons = document.querySelectorAll<HTMLElement>('.js-tab-button');
  tabButtons.forEach(button => {
    button.remove();
  });

  const tabPanels = document.querySelectorAll<HTMLElement>('.mega-menu-content-wrapper');
  tabPanels.forEach(panel => {
    panel.removeAttribute(ATTRIBUTES.ROLE);
    panel.removeAttribute(ATTRIBUTES.ARIA_LABELLEDBY);
    panel.removeAttribute(ATTRIBUTES.ARIA_HIDDEN);
    panel.removeAttribute(ATTRIBUTES.ID);
    panel.removeAttribute('style');
    panel.removeAttribute(ATTRIBUTES.TABINDEX);
  });
}

function createNavigation(): void {
  document.addEventListener('keydown', (event) => {
    if (event.which === KEY_CODES.ESCAPE) {
      if (document.documentElement.classList.contains(CLASSES.NAV_ACTIVE)) {
        const activeButtons = document.querySelectorAll<HTMLElement>('.nav-button.is-active');
        activeButtons.forEach(button => button.click());
        
        const mobileNavIcon = document.querySelector<HTMLElement>('#mobile-nav-icon-2');
        if (mobileNavIcon) {
          mobileNavIcon.click();
        }
      }

      if (document.documentElement.classList.contains('search-active')) {
        const mobileSearchIcon = document.querySelector<HTMLElement>('#mobile-search-icon-2');
        if (mobileSearchIcon) {
          mobileSearchIcon.click();
        }
      }

      closeDesktopNavItem();

      const countryButton = document.querySelector<HTMLElement>('.selected-country-button.is-active');
      if (countryButton) {
        countryButton.click();
      }
    }
  });
}

function createStickyNav(): void {
  const stickyTarget = document.querySelector<HTMLElement>('.js-sticky-target');
  if (!stickyTarget) return;

  const stickyNavTop = stickyTarget.getBoundingClientRect().top + window.pageYOffset;

  const stickyNav = () => {
    const scrollTop = window.pageYOffset || document.documentElement.scrollTop || document.body.scrollTop || 0;

    if (scrollTop > stickyNavTop) {
      stickyTarget.classList.add('sticky-nav');

      closeDesktopNavItem();

      const countryButton = document.querySelector<HTMLElement>('.selected-country-button.is-active');
      if (countryButton) {
        countryButton.click();
      }
    } else {
      stickyTarget.classList.remove('sticky-nav');
    }
  };

  stickyNav();

  window.addEventListener('scroll', stickyNav, { passive: true });
}

export default { init };